import { useRecoilValue } from 'recoil';

import { SHELL_PORTAL } from 'lib/context/ShellContext/shellPortal';
import { useShellPortal } from 'lib/context/ShellContext/ShellContext';
import { forgetSessionState, sessionPersistEffect } from 'lib/helpers/sessionPersistedAtom';

import { currentPractice } from 'components/practice/practiceState';

const storageKeys = new Set();
const loadedWorkspaces = new Set();

/**
 * Where the dock's tabs and the open charts live. The practice portal keeps a workspace per
 * practice, so switching back to a practice in the same browser tab brings back its tabs and
 * charts. The admin portal keeps one across the practices it views; its tabs name their practice.
 */
export const dockWorkspaceOf = (portal, practiceId) =>
  portal === SHELL_PORTAL.PRACTICE && practiceId ? `${portal}:${practiceId}` : portal;

const portalOf = (workspace) => String(workspace).split(':')[0];

export const useDockWorkspace = () => {
  const portal = useShellPortal();
  const practiceId = useRecoilValue(currentPractice)?.id;
  return dockWorkspaceOf(portal, practiceId);
};

// Effects for an atom family keyed by workspace. Each workspace persists under its own key, which
// already names its practice, for the portal's signed-in account.
export const persistPerWorkspace = ({ storageKey, sanitize, revive }) => {
  storageKeys.add(storageKey);
  return (workspace) => [
    () => {
      loadedWorkspaces.add(workspace);
    },
    sessionPersistEffect({
      storageKey: `${storageKey}:${workspace}`,
      sanitize,
      revive,
      owner: portalOf(workspace),
      practiceScoped: false
    })
  ];
};

export const loadedWorkspacesOf = (portal) =>
  [...loadedWorkspaces].filter((workspace) => portalOf(workspace) === portal);

// Includes the workspaces of practices visited earlier in this browser tab, which no atom holds.
export const forgetSavedWorkspaces = (portal) =>
  storageKeys.forEach((storageKey) => forgetSessionState(`${storageKey}:${portal}`));
